import { jwtDecode } from 'jwt-decode';
import type { StateCreator } from 'zustand/vanilla';

import type { UserProfile } from '@/interfaces/user';
import { getWorkspaceStoreState } from '@/store/workspace';
import { queryClient } from '@/utils/queryClient';

import type { UserStore } from '../../store';

interface DecodedToken {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  username?: string;
  actions?: string[];
  roles?: string[];
  exp: number;
}

function buildUserProfile(decoded: DecodedToken): UserProfile {
  return {
    user: {
      id: decoded.id,
      email: decoded.email,
      firstName: decoded.first_name || '',
      lastName: decoded.last_name || '',
      createdAt: new Date(),
      updatedAt: new Date(),
    },
    roles: decoded.roles || [],
  };
}

export interface UserAuthAction {
  /**
   * Universal login method
   */
  login: (token: string, userProfile?: UserProfile) => void;
  /**
   * Universal logout method
   */
  logout: () => void;
  /**
   * Save authentication tokens (access + refresh) and hydrate profile from JWT
   */
  saveToken: (tokens: { accessToken: string; refreshToken?: string }) => void;
  /**
   * Set authentication loading state
   */
  setAuthLoading: (isLoading: boolean) => void;
  /**
   * Set authentication error
   */
  setAuthError: (error?: string) => void;
  /**
   * Update current user profile
   */
  updateUserProfile: (userProfile: UserProfile) => void;
  /**
   * Mark the user as authenticated without a token (cookie-based auth).
   * Use when the backend has already set the session cookie.
   */
  setAuthenticated: (userProfile?: UserProfile) => void;
}

export const createAuthSlice: StateCreator<UserStore, [['zustand/persist', unknown], ['zustand/devtools', never]], [], UserAuthAction> = (
  set,
) => ({
  login: (token: string, userProfile?: UserProfile) => {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      const profile = userProfile || buildUserProfile(decoded);

      set({
        token,
        isAuthenticated: true,
        isLoading: false,
        error: undefined,
        currentUser: profile,
      });
    } catch (_error) {
      set({
        error: 'Invalid token',
        isLoading: false,
        isAuthenticated: false,
      });
    }
  },

  saveToken: ({ accessToken, refreshToken }: { accessToken: string; refreshToken?: string }) => {
    try {
      const decoded = jwtDecode<DecodedToken>(accessToken);
      const profile = buildUserProfile(decoded);

      set({
        token: accessToken,
        refreshToken: refreshToken ?? undefined,
        isAuthenticated: true,
        isLoading: false,
        error: undefined,
        currentUser: profile,
      });
    } catch (_error) {
      set({
        error: 'Invalid token',
        isLoading: false,
        isAuthenticated: false,
      });
    }
  },

  logout: () => {
    queryClient.clear();
    getWorkspaceStoreState().clearCurrentSpace();
    getWorkspaceStoreState().clearPrivateSpace();

    set({
      token: undefined,
      refreshToken: undefined,
      isAuthenticated: false,
      currentUser: undefined,
      error: undefined,
      isLoading: false,
      privateSpaceId: '',
      termsAccepted: false,
      analyticsAccepted: false,
    });
  },

  setAuthLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  setAuthError: (error?: string) => {
    set({ error, isLoading: false });
  },

  updateUserProfile: (userProfile: UserProfile) => {
    set({ currentUser: userProfile });
  },

  setAuthenticated: (userProfile?: UserProfile) => {
    set({
      isAuthenticated: true,
      isLoading: false,
      error: undefined,
      currentUser: userProfile,
      token: undefined,
      refreshToken: undefined,
    });
  },
});
